// src/context/ThemeContext.tsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { Language } from "../types/api";

interface ThemeContextProps {
  darkMode: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    // Check saved preference
    const savedTheme = localStorage.getItem("theme");

    // Check system preference
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;

    // Return saved preference or system preference
    return savedTheme ? savedTheme === "dark" : prefersDark;
  });

  // Save theme when it changes
  useEffect(() => {
    localStorage.setItem("theme", darkMode ? "dark" : "light");
    document.body.setAttribute("data-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // Sync API language with i18n language
  useEffect(() => {
    const currentLang = i18n.language.split("-")[0]; // 'pt-BR' -> 'pt'

    // Update API language when i18n language changes
    if (currentLang === "pt" || currentLang === "en" || currentLang === "es") {
      api.setLanguage(currentLang as Language);
    }
  }, [i18n.language]);

  const toggleTheme = () => {
    setDarkMode(prevMode => !prevMode);
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export default ThemeProvider;
